import React, { useRef, useEffect, useState } from 'react';
import useIsInViewport from 'use-is-in-viewport';

import { useAppContext } from '@prisjakt/ui-app-context';

import ThumbsUpIcon from 'mdi-react/ThumbUpIcon';
import ThumbsUpOutlineIcon from 'mdi-react/ThumbUpOutlineIcon';

import Rating from '@prisjakt/ui-rating';
import ReviewActions from './review-actions';
import ReviewText from './review-text';
import User from './user';

import { Translation, Reply, Review } from './types';

import * as S from './styles';

type Props = {
  id: string;
  rating?: number | null;
  date?: string | null;
  editDate?: string | null;
  purchaseDate?: string | null;
  avatar: React.ReactElement;
  displayName?: string | null;
  userReviewCount?: number | null;
  text?: string | null;
  translation?: Translation | null;
  reply?: Reply | null;
  styling?: 'default' | 'archived';
  editControl?: React.ReactElement | null;
  actions: {
    name: string;
    handler: (review: Review) => void;
  }[];
  className?: string;
  trackViewed?: () => void;
  userId?: string | null;
  toggleVote?: () => void;
  liked?: boolean;
  isMyReview: boolean;
};

const UserReviewRow = ({
  id,
  rating = 0,
  date,
  editDate,
  purchaseDate,
  avatar,
  displayName,
  userReviewCount,
  text,
  translation,
  reply,
  styling = 'default',
  editControl,
  actions,
  className,
  trackViewed,
  userId,
  toggleVote,
  liked = false,
  isMyReview,
}: Props) => {
  const { t } = useAppContext();
  const userReviewRef = useRef(null);
  const [reviewViewed, setReviewViewed] = useState(false);

  const [isInViewport, wrappedTargetRef] = useIsInViewport({
    target: userReviewRef,
  });

  useEffect(() => {
    if (trackViewed && isInViewport && !reviewViewed) {
      setReviewViewed(true);
      trackViewed();
    }
  }, [isInViewport]);

  return (
    <S.UserReviewRow
      className={className || ''}
      ref={wrappedTargetRef}
      isMyReview={isMyReview}
    >
      <S.RatingWrapper>
        <Rating
          rate={rating}
          showCounter={false}
          secondaryColors={styling === 'archived'}
        />
        <S.DateLabel>{date}</S.DateLabel>
        <div>
          {editDate && (
            <S.DateLabel editDate>
              {t('reviews', 'info.last-edited', { date: editDate })}
            </S.DateLabel>
          )}
          {purchaseDate && (
            <S.DateLabel purchaseDate>
              {t('reviews', 'info.purchased', { date: purchaseDate })}
            </S.DateLabel>
          )}
        </div>
      </S.RatingWrapper>
      <S.MainBlock>
        {editControl ? <S.Edit>{editControl}</S.Edit> : null}
        {actions && actions.length && (
          <ReviewActions actions={actions} id={id} />
        )}
        {displayName && (
          <User
            id={userId}
            avatar={avatar}
            displayName={displayName}
            userReviewCount={userReviewCount}
          />
        )}
        <ReviewText
          id={id}
          translation={translation}
          text={text}
          reply={reply}
        />
        {toggleVote && !isMyReview ? (
          <S.HelpfulButton onClick={toggleVote} liked={liked}>
            {liked ? (
              <ThumbsUpIcon size={16} />
            ) : (
              <ThumbsUpOutlineIcon size={16} />
            )}
            <span>{t('reviews', 'button.helpful')}</span>
          </S.HelpfulButton>
        ) : null}
      </S.MainBlock>
    </S.UserReviewRow>
  );
};

export default UserReviewRow;
